import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Target } from 'lucide-react';
import { useReadingGoals } from '@/hooks/useReadingGoals';

interface ManageReadingGoalDialogProps {
  year?: number;
  currentGoal?: number;
  onGoalUpdated?: () => void;
  trigger?: React.ReactNode;
}

export function ManageReadingGoalDialog({ 
  year = new Date().getFullYear(), 
  currentGoal, 
  onGoalUpdated,
  trigger 
}: ManageReadingGoalDialogProps) {
  const [open, setOpen] = useState(false);
  const [targetBooks, setTargetBooks] = useState(currentGoal || 12);
  const { loading, createOrUpdateGoal } = useReadingGoals();

  useEffect(() => {
    if (currentGoal !== undefined) {
      setTargetBooks(currentGoal);
    }
  }, [currentGoal]);

  const handleSave = async () => {
    await createOrUpdateGoal(year, targetBooks);
    setOpen(false);
    onGoalUpdated?.();
  };

  const presets = [12, 24, 36, 52, 100];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Target className="w-4 h-4 mr-2" />
            {currentGoal ? 'Edit Goal' : 'Set Goal'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Reading Goal for {year}</DialogTitle>
          <DialogDescription>
            How many books do you want to read this year?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="target-books">Target Books</Label>
            <Input
              id="target-books"
              type="number"
              min="1"
              max="1000"
              value={targetBooks}
              onChange={(e) => setTargetBooks(parseInt(e.target.value) || 12)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Quick Presets</Label>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset}
                  variant={targetBooks === preset ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTargetBooks(preset)}
                >
                  {preset} books
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Goal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
